import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { finalize, switchMap } from "rxjs/operators";
import { MaiaColumnMappingService } from "../../data-access-layer/maia/maia-column-mapping.service";
import { FieldMappingDecision, SovFieldMapping } from "../../model/maia/sov-field-mapping";

/**
 * MAIA wizard — Prepare your SOV (macro step "Save to MAPTYCS", micro
 * "Step 4 of 4").
 *
 * Shows how MAIA matched each column of the uploaded file to a Maptycs
 * field. The MAIA Field value is a required free-text box (not a dropdown —
 * there's no fixed field list to constrain it to); "Accept and Continue" is
 * disabled until every row has one.
 *
 * The Action column (accept/skip/edit per row) is temporarily hidden —
 * `showActionColumn` — while the flow only needs a single confirm step. The
 * underlying decision/edit/skip logic is kept, not deleted, so re-enabling
 * it later is just flipping that flag back. Rows flagged `rootField`,
 * `coordinateField` or `idField` (system-managed columns) are similarly
 * hidden from the table — via `visibleRows`, not filtered out of
 * `mappings`/`rows` — since they're expected to need showing again later too.
 *
 * `mappings` comes from `MaiaColumnMappingService.getFieldMappings()`
 * (`POST /sov/{jobId}/column-mapping`) — nothing in this component reads
 * placeholder data directly.
 *
 * JIRA-SOV-015: the box shows and edits the readable LABEL
 * (`maiaFieldLabel`, e.g. "Total Insured Value"); the field id that gets
 * saved (`maiaFieldId`, e.g. `total_insured_value`) is shown underneath and
 * derived by the backend from the label on each save. Each edit is saved when
 * the user leaves the field (blur / Enter) via
 * `PATCH /sov/{jobId}/column-mapping`, then the whole mapping is
 * reloaded — the backend keeps names unique, so saving one row can give
 * another row a new AI-suggested name (shown with its note). Rows flagged
 * `locked` (e.g. a field the dataset already has) are shown read-only. A
 * rejected edit shows the backend's message and reverts the row.
 */
@Component({
    selector: "maia-prepare-step",
    templateUrl: "./maia-prepare-step.component.html",
    styleUrls: ["./maia-prepare-step.component.scss"],
})
export class MaiaPrepareStepComponent implements OnChanges {
    /** The match results. */
    @Input() mappings: SovFieldMapping[] = [];

    /**
     * Total columns found in the file. Drives "N of M fields mapped"; falls
     * back to the number of rows when not supplied.
     */
    @Input() totalFields: number | null = null;

    /** JIRA-SOV-015: the job whose mapping is shown — needed to save each edit. */
    @Input() jobId: number | null = null;

    /** The card's close (×). */
    @Output() readonly closeRequested = new EventEmitter<boolean>();

    /** "Back" — returns to the previous step. Emitter kept even though the button is hidden — see class doc. */
    @Output() readonly back = new EventEmitter<void>();

    /** "Accept and Continue" — emits the mappings as they now stand. */
    @Output() readonly accepted = new EventEmitter<SovFieldMapping[]>();

    readonly FieldMappingDecision = FieldMappingDecision;

    /** Temporarily off — see class doc. */
    showActionColumn = false;

    /** Working copy, so editing doesn't mutate the caller's array. */
    rows: SovFieldMapping[] = [];

    /** An edit is being saved — "Accept and Continue" waits for it. */
    saving = false;

    /** Why the last edit was rejected (e.g. the name belongs to a locked column); null when there's nothing to show. */
    errorMessage: string | null = null;

    /** Each row's label as last saved on the server, keyed by source column — what a rejected edit reverts to. */
    private savedLabels = new Map<string, string>();

    constructor(private readonly columnMappingService: MaiaColumnMappingService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.mappings) {
            this.setRows(this.mappings || []);
        }
    }

    private setRows(mappings: SovFieldMapping[]): void {
        this.rows = mappings.map((mapping) => ({ ...mapping }));
        this.savedLabels = new Map(this.rows.map((row) => [row.sourceField, (row.maiaFieldLabel || "").trim()]));
    }

    /**
     * `rows` minus the system-managed root/coordinate columns — hidden from
     * the table for now (not filtered out of `rows` itself, see class doc),
     * so the user never sees or edits them.
     */
    get visibleRows(): SovFieldMapping[] {
        return this.rows.filter((row) => !row.rootField && !row.coordinateField && !row.idField);
    }

    /** "N of M fields mapped successfully" — counted against what the user actually sees. */
    get mappedCount(): number {
        return this.visibleRows.filter(
            (row) => row.decision !== FieldMappingDecision.SKIPPED && !!row.maiaFieldId
        ).length;
    }

    get fieldTotal(): number {
        return this.totalFields === null ? this.visibleRows.length : this.totalFields;
    }

    /** Every MAIA Field is required — true once none of the visible rows are blank. */
    get canContinue(): boolean {
        return (
            !this.saving &&
            this.visibleRows.length > 0 &&
            this.visibleRows.every((row) => !!row.maiaFieldLabel && row.maiaFieldLabel.trim().length > 0)
        );
    }

    /** MAIA Field text box changed — the user edits the label; the id follows when it is saved. */
    onFieldInput(row: SovFieldMapping, value: string): void {
        row.maiaFieldLabel = value;
        row.decision = value.trim() ? FieldMappingDecision.ACCEPTED : FieldMappingDecision.SKIPPED;
    }

    /**
     * JIRA-SOV-015: the user left the field (blur / Enter) — save a changed,
     * non-blank name and reload, since the backend may have renamed another
     * column that already used it. Locked rows are read-only and never saved.
     */
    commitField(row: SovFieldMapping): void {
        const value = (row.maiaFieldLabel || "").trim();
        if (row.locked || this.jobId === null || this.saving || !value || value === this.savedLabels.get(row.sourceField)) {
            return;
        }
        const jobId = this.jobId;
        this.saving = true;
        this.errorMessage = null;
        this.columnMappingService
            .updateFieldMapping(jobId, row.sourceField, value)
            .pipe(
                switchMap(() => this.columnMappingService.reloadFieldMappings(jobId)),
                finalize(() => (this.saving = false))
            )
            .subscribe({
                next: (mappings) => this.setRows(mappings),
                error: (error) => {
                    this.errorMessage = this.extractErrorMessage(error);
                    row.maiaFieldLabel = this.savedLabels.get(row.sourceField) || null;
                    row.decision = FieldMappingDecision.ACCEPTED;
                },
            });
    }

    /** "Edit" — reopens the row. Only reachable once showActionColumn is back on. */
    edit(row: SovFieldMapping): void {
        row.decision = FieldMappingDecision.EDITING;
    }

    skip(row: SovFieldMapping): void {
        row.maiaFieldId = null;
        row.maiaFieldLabel = null;
        row.decision = FieldMappingDecision.SKIPPED;
    }

    requestClose(): void {
        this.closeRequested.emit(true);
    }

    acceptAndContinue(): void {
        if (this.canContinue) {
            this.accepted.emit(this.rows.map((row) => ({ ...row })));
        }
    }

    /** Same extraction as the review step: the backend's own sentence when it sent one, a generic line otherwise. */
    private extractErrorMessage(error: any): string {
        const body = error && error.error;
        const fallback = "We couldn't save that field name. Please try again.";
        if (!body) {
            return fallback;
        }
        if (typeof body === "string" && body.trim()) {
            return body.trim();
        }
        if (Array.isArray(body.errors)) {
            const reasons = body.errors
                .filter((reason: unknown) => typeof reason === "string" && reason.trim())
                .map((reason: string) => reason.trim());
            if (reasons.length) {
                return reasons.join(" ");
            }
        }
        const message = body.message || body.error;
        return (typeof message === "string" && message.trim()) || fallback;
    }
}
